import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import { Track, MixPreset, AudioFile, PlaybackMode } from '../types';
import { staticAudioFiles, categories, getAudioFiles, loadAllOtherAudios } from '../data/audioCategories';
import AudioEditorModal from './AudioEditorModal';

interface AudioPlayerProps {
  pomodoroMode?: string;
  linkWithWhiteNoise?: boolean;
  onPomodoroModeChange?: (mode: string) => void;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ pomodoroMode, linkWithWhiteNoise = true, onPomodoroModeChange }) => {
  // 記錄用戶手動暫停狀態
  const [userPaused, setUserPaused] = useState<boolean>(false);
  // 載入自定義音訊
  const loadCustomAudios = (): AudioFile[] => {
    try {
      const customAudiosJson = localStorage.getItem('customAudios');
      return customAudiosJson ? JSON.parse(customAudiosJson) : [];
    } catch (err) {
      console.error('Error loading custom audios:', err);
      return [];
    }
  };

  const [customAudios, setCustomAudios] = useState<AudioFile[]>(loadCustomAudios());
  
  const [tracks, setTracks] = useState<Track[]>(
    [...staticAudioFiles, ...customAudios].map(file => ({
      ...file,
      isPlaying: false,
      volume: 0.5,
      locked: false,
      // 根據類別設置默認播放模式
      playbackMode: file.category === 'other' ? PlaybackMode.INTERVAL : 
                   (file.name.includes('電話') || file.name.includes('對話')) ? PlaybackMode.INTERVAL : 
                   PlaybackMode.LOOP
    }))
  );

  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [mixPresets, setMixPresets] = useState<MixPreset[]>([]);
  const [currentPresetName, setCurrentPresetName] = useState<string>('');
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [excludedCategories, setExcludedCategories] = useState<string[]>(['binaural', 'isochronic', 'other']);
  const [showExcludeDialog, setShowExcludeDialog] = useState<boolean>(false);

  const audioElementsRef = useRef<{[key: string]: HTMLAudioElement}>({});

  // 從 localStorage 載入混音預設和自定義音訊，並加載其他音訊
  useEffect(() => {
    // 加載混音預設
    const savedPresets = localStorage.getItem('mixPresets');
    if (savedPresets) {
      setMixPresets(JSON.parse(savedPresets));
    }
    
    // 載入自定義音訊
    const loadedCustomAudios = loadCustomAudios();
    if (loadedCustomAudios.length > 0) {
      setCustomAudios(loadedCustomAudios);
      
      // 將自定義音訊添加到音軌列表
      setTracks(prevTracks => {
        // 過濾掉已存在的自定義音訊（避免重複）
        const existingCustomPaths = new Set(
          prevTracks.filter(t => t.isCustom).map(t => t.path)
        );
        
        const newCustomTracks = loadedCustomAudios
          .filter(audio => !existingCustomPaths.has(audio.path))
          .map(audio => ({
            ...audio,
            isPlaying: false,
            volume: 0.5,
            locked: false,
            // 根據名稱設置默認播放模式
            playbackMode: (audio.name.includes('電話') || audio.name.includes('對話')) ? 
                         PlaybackMode.INTERVAL : PlaybackMode.LOOP
          }));
        
        return [...prevTracks, ...newCustomTracks];
      });
    }
    
    // 加載其他音訊檔案
    const loadOtherAudioFiles = async () => {
      await loadAllOtherAudios();
      const otherAudios = getAudioFiles().filter(audio => audio.category === 'other');
      
      if (otherAudios.length > 0) {
        // 將其他音訊添加到音軌列表
        setTracks(prevTracks => {
          // 過濾掉已存在的其他音訊（避免重複）
          const existingOtherPaths = new Set(
            prevTracks.filter(t => t.category === 'other').map(t => t.path)
          );
          
          const newOtherTracks = otherAudios
            .filter(audio => !existingOtherPaths.has(audio.path))
            .map(audio => ({
              ...audio,
              isPlaying: false,
              volume: 0.5,
              locked: false,
              // 對話類音訊使用間隔播放模式
              playbackMode: (audio.name.includes('電話') || audio.name.includes('對話') || 
                           audio.name.includes('karina')) ? 
                           PlaybackMode.INTERVAL : PlaybackMode.LOOP
            }));
          
          return [...prevTracks, ...newOtherTracks];
        });
      }
    };
    
    loadOtherAudioFiles();
  }, []);

  // 保存混音預設到 localStorage
  useEffect(() => {
    if (mixPresets.length > 0) {
      localStorage.setItem('mixPresets', JSON.stringify(mixPresets));
    }
  }, [mixPresets]);

  // 設置音訊播放模式
  const setupAudioPlayback = (audio: HTMLAudioElement, track: Track) => {
    const playbackMode = track.playbackMode || 
      // 根據類別自動設置播放模式
      (track.category === 'other' ? PlaybackMode.ONCE : PlaybackMode.LOOP);
    
    switch (playbackMode) {
      case PlaybackMode.LOOP:
        // 標準循環播放
        audio.loop = true;
        break;
        
      case PlaybackMode.SMART_LOOP:
        // 智能循環播放 - 添加隨機變化
        audio.loop = true;
        
        // 監聽音訊結束事件，添加隨機變化
        audio.onended = () => {
          // 隨機調整音量 (±40%)
          if (!track.locked) {
            const volumeVariation = track.volume * (0.6 + Math.random() * 0.8);
            audio.volume = Math.min(1, Math.max(0, volumeVariation));
          }
          
          // 有 20% 的機率暫停 1-60 秒後再播放
          if (Math.random() < 0.2) {
            audio.pause();
            setTimeout(() => {
              if (track.isPlaying) {
                audio.play().catch(err => console.error("重新播放失敗:", err));
              }
            }, 1000 + Math.random() * 59000); // 1-60秒
          }
        };
        break;
        
      case PlaybackMode.ONCE:
        // 只播放一次
        audio.loop = false;
        audio.onended = () => {
          const trackIndex = tracks.findIndex(t => t.path === track.path);
          if (trackIndex !== -1) {
            const newTracks = [...tracks];
            newTracks[trackIndex].isPlaying = false;
            setTracks(newTracks);
          }
        };
        break;
        
      case PlaybackMode.INTERVAL:
        // 間隔播放 - 播放一次後等待一段時間再播放
        audio.loop = false;
        audio.onended = () => {
          // 使用自定義間隔設定或默認值
          const minDelay = track.intervalSettings?.minDelay || 5;
          const maxDelay = track.intervalSettings?.maxDelay || 15;
          const delayRange = maxDelay - minDelay;
          
          // 等待指定秒數後再次播放
          setTimeout(() => {
            if (track.isPlaying) {
              audio.currentTime = 0;
              audio.play().catch(err => console.error("間隔播放失敗:", err));
            }
          }, (minDelay + Math.random() * delayRange) * 1000);
        };
        break;
    }
  };

  const togglePlay = (index: number) => {
    const newTracks = [...tracks];
    const track = newTracks[index];
    
    // 懶加載：只有在播放時才創建音頻元素
    if (!audioElementsRef.current[track.path] && !track.isPlaying) {
      let audio: HTMLAudioElement;
      
      // 處理自定義音訊
      if (track.isCustom && track.audioData) {
        audio = new Audio(track.audioData);
      } else {
        audio = new Audio(track.path);
      }
      
      // 設置音量
      audio.volume = track.volume;
      
      // 設置播放模式
      setupAudioPlayback(audio, track);
      
      audioElementsRef.current[track.path] = audio;
    }
    
    const audio = audioElementsRef.current[track.path];
    track.isPlaying = !track.isPlaying;
    
    if (track.isPlaying) {
      audio.play().catch(error => {
        console.error("播放失敗:", error);
        track.isPlaying = false;
        setTracks([...newTracks]);
      });
    } else {
      audio.pause();
    }

    setTracks(newTracks);
  };

  const handleVolumeChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    const newTracks = [...tracks];
    newTracks[index].volume = newVolume;
    
    const audio = audioElementsRef.current[newTracks[index].path];
    if (audio) {
      audio.volume = newVolume;
    }
    
    setTracks(newTracks);
  };

  // 切換音軌鎖定狀態
  const toggleLock = (index: number) => {
    const newTracks = [...tracks];
    newTracks[index].locked = !newTracks[index].locked;
    setTracks(newTracks);
  };

  // 儲存當前混音設定
  const saveMixPreset = () => {
    if (!currentPresetName.trim()) return;

    const activeTracks = tracks.filter(track => track.isPlaying);
    if (activeTracks.length === 0) return;

    const newPreset: MixPreset = {
      id: Date.now().toString(),
      name: currentPresetName,
      tracks: activeTracks.map(track => ({
        path: track.path,
        volume: track.volume,
        playbackMode: track.playbackMode,
        intervalSettings: track.intervalSettings
      }))
    };

    setMixPresets([...mixPresets, newPreset]);
    setCurrentPresetName('');
    setShowSaveDialog(false);
  };

  // 載入混音預設
  const loadMixPreset = (preset: MixPreset) => {
    // 先停止所有非鎖定的音軌
    tracks.forEach(track => {
      if (track.isPlaying && !track.locked) {
        const audio = audioElementsRef.current[track.path];
        if (audio) {
          audio.pause();
        }
      }
    });

    // 重設所有非鎖定的音軌狀態
    const newTracks = tracks.map(track => ({
      ...track,
      isPlaying: track.locked ? track.isPlaying : false,
      volume: track.locked ? track.volume : 0.5
    }));

    // 設定預設中的音軌，並將它們移到列表前面
    const activatedTracks: Track[] = [];
    const remainingTracks: Track[] = [];

    // 設定預設中的音軌
    preset.tracks.forEach(presetTrack => {
      const trackIndex = newTracks.findIndex(t => t.path === presetTrack.path);
      if (trackIndex !== -1 && !newTracks[trackIndex].locked) {
        const updatedTrack = {
          ...newTracks[trackIndex],
          isPlaying: true,
          volume: presetTrack.volume,
          playbackMode: presetTrack.playbackMode || newTracks[trackIndex].playbackMode,
          intervalSettings: presetTrack.intervalSettings || newTracks[trackIndex].intervalSettings
        };
        
        activatedTracks.push(updatedTrack);

        // 創建並播放音頻
        if (!audioElementsRef.current[presetTrack.path]) {
          let audio: HTMLAudioElement;
          
          // 處理自定義音訊
          if (updatedTrack.isCustom && updatedTrack.audioData) {
            audio = new Audio(updatedTrack.audioData);
          } else {
            audio = new Audio(presetTrack.path);
          }
          
          audio.volume = presetTrack.volume;
          
          // 設置播放模式
          setupAudioPlayback(audio, updatedTrack);
          
          audioElementsRef.current[presetTrack.path] = audio;
        }

        const audio = audioElementsRef.current[presetTrack.path];
        audio.volume = presetTrack.volume;
        audio.play().catch(error => console.error("載入預設時播放失敗:", error));
      }
    });

    // 將其他音軌添加到剩餘列表
    newTracks.forEach(track => {
      if (!activatedTracks.some(t => t.path === track.path)) {
        remainingTracks.push(track);
      }
    });

    // 更新音軌列表，將激活的音軌放在前面
    setTracks([...activatedTracks, ...remainingTracks]);
  };

  // 刪除混音預設
  const deleteMixPreset = (id: string) => {
    const updatedPresets = mixPresets.filter(preset => preset.id !== id);
    setMixPresets(updatedPresets);
    
    // 直接更新 localStorage
    localStorage.setItem('mixPresets', JSON.stringify(updatedPresets));
  };

  // 清除當前混音（停止所有非鎖定的音軌）
  const clearCurrentMix = () => {
    const newTracks = [...tracks];
    
    newTracks.forEach((track, index) => {
      if (track.isPlaying && !track.locked) {
        track.isPlaying = false;
        const audio = audioElementsRef.current[track.path];
        if (audio) {
          audio.pause();
        }
      }
    });
    
    setTracks(newTracks);
  };

  // 切換類別排除狀態
  const toggleExcludeCategory = (categoryId: string) => {
    if (excludedCategories.includes(categoryId)) {
      setExcludedCategories(excludedCategories.filter(id => id !== categoryId));
    } else {
      setExcludedCategories([...excludedCategories, categoryId]);
    }
  };

  // 隨機混音功能
  const randomMix = () => {
    // 先停止所有非鎖定的音軌
    tracks.forEach(track => {
      if (track.isPlaying && !track.locked) {
        const audio = audioElementsRef.current[track.path];
        if (audio) {
          audio.pause();
        }
      }
    });

    // 重設所有非鎖定的音軌狀態
    const newTracks = tracks.map(track => ({
      ...track,
      isPlaying: track.locked ? track.isPlaying : false,
      volume: track.locked ? track.volume : 0.5
    }));

    // 從每個未排除的分類中隨機選擇一個音軌
    const selectedTracks: Track[] = [];
    const remainingTracks: Track[] = [];
    const selectedCategories = new Set<string>();
    
    categories.forEach(category => {
      // 跳過被排除的類別
      if (excludedCategories.includes(category.id)) return;
      
      const categoryTracks = tracks.filter(track => 
        track.category === category.id && !track.locked
      );
      
      if (categoryTracks.length > 0) {
        const randomIndex = Math.floor(Math.random() * categoryTracks.length);
        const selectedTrack = categoryTracks[randomIndex];
        const trackIndex = newTracks.findIndex(t => t.path === selectedTrack.path);
        
        if (trackIndex !== -1) {
          const updatedTrack = {...newTracks[trackIndex]};
          updatedTrack.isPlaying = true;
          
          // 為特定類別設置智能循環模式
          if (['nature', 'ambient', 'noise'].includes(selectedTrack.category)) {
            updatedTrack.playbackMode = PlaybackMode.SMART_LOOP;
          } else if (selectedTrack.category === 'other') {
            // 對話或特殊音效使用間隔播放
            updatedTrack.playbackMode = PlaybackMode.INTERVAL;
          }
          
          selectedTracks.push(updatedTrack);
          
          // 創建並播放音頻
          if (!audioElementsRef.current[selectedTrack.path]) {
            let audio: HTMLAudioElement;
            
            // 處理自定義音訊
            if (selectedTrack.isCustom && selectedTrack.audioData) {
              audio = new Audio(selectedTrack.audioData);
            } else {
              audio = new Audio(selectedTrack.path);
            }
            
            audio.volume = 0.5;
            
            // 設置播放模式
            setupAudioPlayback(audio, updatedTrack);
            
            audioElementsRef.current[selectedTrack.path] = audio;
          }

          const audio = audioElementsRef.current[selectedTrack.path];
          audio.play().catch(error => console.error("隨機混音播放失敗:", error));
          
          selectedCategories.add(category.id);
        }
      }
    });
    
    // 將其他音軌添加到剩餘列表
    newTracks.forEach(track => {
      if (!selectedTracks.some(t => t.path === track.path)) {
        remainingTracks.push(track);
      }
    });

    // 更新音軌列表，將選中的音軌放在前面
    setTracks([...selectedTracks, ...remainingTracks]);
  };

  // 過濾顯示的音軌
  const filteredTracks = activeCategory === 'all' 
    ? tracks 
    : tracks.filter(track => track.category === activeCategory);
    
  // 處理間隔設定的保存
  const saveIntervalSettings = () => {
    if (selectedTrackForInterval === null) return;
    
    const newTracks = [...tracks];
    newTracks[selectedTrackForInterval].intervalSettings = {
      minDelay: intervalMinDelay,
      maxDelay: intervalMaxDelay
    };
    
    // 如果音軌正在播放，需要重新設置播放模式
    if (newTracks[selectedTrackForInterval].isPlaying) {
      const audio = audioElementsRef.current[newTracks[selectedTrackForInterval].path];
      if (audio) {
        // 清除之前的事件監聽器
        audio.onended = null;
        // 設置新的播放模式
        setupAudioPlayback(audio, newTracks[selectedTrackForInterval]);
      }
    }
    
    setTracks(newTracks);
    setShowIntervalSettings(false);
  };
  
  // 全局暫停/播放控制
  const toggleGlobalPlayback = () => {
    const newState = !globalPlaybackState;
    setGlobalPlaybackState(newState);
    
    // 如果是手動暫停，記錄狀態
    if (!newState) {
      setUserPaused(true);
    } else {
      setUserPaused(false);
    }
    
    // 更新所有正在播放的音軌
    tracks.forEach(track => {
      if (track.isPlaying) {
        const audio = audioElementsRef.current[track.path];
        if (audio) {
          if (newState) {
            audio.play().catch(error => console.error("全局播放失敗:", error));
          } else {
            audio.pause();
          }
        }
      }
    });
  };
  
  // 監聽番茄鐘模式變化
  useEffect(() => {
    // 只有在連動白噪音開啟時才響應番茄鐘模式變化
    if (linkWithWhiteNoise) {
      if (pomodoroMode === 'focus') {
        // 專注模式開始時，只有在非用戶手動暫停的情況下才恢復播放
        if (!globalPlaybackState && !userPaused) {
          toggleGlobalPlayback();
        }
      } else if (pomodoroMode === 'shortBreak' || pomodoroMode === 'longBreak') {
        // 休息模式開始時，暫停播放並記錄當前是否為用戶手動暫停
        if (globalPlaybackState) {
          // 記錄當前不是用戶手動暫停
          setUserPaused(false);
          toggleGlobalPlayback();
        }
      }
    }
  }, [pomodoroMode, linkWithWhiteNoise]);

  // 獲取當前播放中的音軌
  const activeTracks = tracks.filter(track => track.isPlaying);

  const [showAudioEditor, setShowAudioEditor] = useState<boolean>(false);
  const [showIntervalSettings, setShowIntervalSettings] = useState<boolean>(false);
  const [selectedTrackForInterval, setSelectedTrackForInterval] = useState<number | null>(null);
  const [intervalMinDelay, setIntervalMinDelay] = useState<number>(5);
  const [intervalMaxDelay, setIntervalMaxDelay] = useState<number>(15);
  const [globalPlaybackState, setGlobalPlaybackState] = useState<boolean>(true);
  
  // 處理添加自定義音訊
  const handleCustomAudioAdded = (newAudio: AudioFile) => {
    // 更新自定義音訊列表
    setCustomAudios(prev => [...prev, newAudio]);
    
    // 添加到音軌列表
    setTracks(prev => [
      ...prev,
      {
        ...newAudio,
        isPlaying: false,
        volume: 0.5,
        locked: false,
        // 根據名稱設置默認播放模式
        playbackMode: (newAudio.name.includes('電話') || newAudio.name.includes('對話') || 
                      newAudio.name.includes('karina')) ? 
                      PlaybackMode.INTERVAL : PlaybackMode.LOOP
      }
    ]);
    
    // 保存到 localStorage - 確保直接從 localStorage 讀取最新數據
    try {
      const storedCustomAudios = JSON.parse(localStorage.getItem('customAudios') || '[]');
      localStorage.setItem('customAudios', JSON.stringify([...storedCustomAudios, newAudio]));
    } catch (err) {
      console.error('Error saving to localStorage:', err);
      // 如果出錯，嘗試直接保存當前狀態
      localStorage.setItem('customAudios', JSON.stringify([...customAudios, newAudio]));
    }
  };
  
  // 刪除自定義音訊
  const deleteCustomAudio = (path: string) => {
    // 確保音訊不在播放中
    const trackIndex = tracks.findIndex(t => t.path === path);
    if (trackIndex !== -1 && tracks[trackIndex].isPlaying) {
      const audio = audioElementsRef.current[path];
      if (audio) {
        audio.pause();
      }
    }
    
    // 從音軌列表中移除
    setTracks(prev => prev.filter(track => track.path !== path));
    
    // 從自定義音訊列表中移除
    const newCustomAudios = customAudios.filter(audio => audio.path !== path);
    setCustomAudios(newCustomAudios);
    
    // 直接從 localStorage 中讀取並更新，確保數據同步
    try {
      const storedCustomAudios = JSON.parse(localStorage.getItem('customAudios') || '[]');
      const updatedStoredAudios = storedCustomAudios.filter((audio: AudioFile) => audio.path !== path);
      localStorage.setItem('customAudios', JSON.stringify(updatedStoredAudios));
    } catch (err) {
      console.error('Error updating localStorage:', err);
    }
    
    // 從音頻元素引用中移除
    if (audioElementsRef.current[path]) {
      delete audioElementsRef.current[path];
    }
  };

  return (
    <div className="audio-player-container w-full max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">
          禪意白噪音
        </h2>
        
        {/* 全局播放控制 */}
        <button
          onClick={toggleGlobalPlayback}
          className={`p-2 rounded-full ${globalPlaybackState ? 'bg-teal-600 hover:bg-teal-700' : 'bg-red-600 hover:bg-red-700'} text-white transition-colors`}
          aria-label={globalPlaybackState ? '全局暫停' : '全局播放'}
          title={globalPlaybackState ? '暫停所有音訊' : '播放所有音訊'}
        >
          {globalPlaybackState ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          )}
        </button>
      </div>
      
      {/* 自定義音訊上傳按鈕 */}
      <div className="mb-6 flex justify-center">
        <button
          onClick={() => setShowAudioEditor(true)}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 rounded-md text-white transition-colors flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          上傳自定義白噪音
        </button>
      </div>
      
      {/* 音訊編輯器彈窗 */}
      <AudioEditorModal
        isOpen={showAudioEditor}
        onClose={() => setShowAudioEditor(false)}
        onSave={handleCustomAudioAdded}
      />
      
      {/* 間隔設定彈窗 */}
      {showIntervalSettings && selectedTrackForInterval !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-md w-full border border-gray-700">
            <h3 className="text-xl font-semibold text-teal-300 mb-4">間隔播放設定</h3>
            <p className="text-gray-300 mb-4">
              設定音訊播放完畢後，等待多久再次播放
            </p>
            
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">最小延遲時間 (秒)</label>
              <input 
                type="number" 
                value={intervalMinDelay} 
                onChange={(e) => setIntervalMinDelay(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-gray-300 mb-2">最大延遲時間 (秒)</label>
              <input 
                type="number" 
                value={intervalMaxDelay} 
                onChange={(e) => setIntervalMaxDelay(Math.max(intervalMinDelay, parseInt(e.target.value) || intervalMinDelay))}
                min={intervalMinDelay}
                className="w-full p-2 bg-gray-700 text-white rounded-md border border-gray-600"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowIntervalSettings(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md"
              >
                取消
              </button>
              <button 
                onClick={saveIntervalSettings}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md"
              >
                儲存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 混音預設控制區 */}
      <div className="mix-presets-container mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-teal-300">混音預設</h3>
          <div className="flex space-x-2">
            <button 
              onClick={() => setShowSaveDialog(!showSaveDialog)}
              className="px-3 py-1 bg-teal-600 hover:bg-teal-700 rounded-md text-white text-sm transition-colors"
            >
              儲存當前混音
            </button>
            <button 
              onClick={() => setShowExcludeDialog(!showExcludeDialog)}
              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-md text-white text-sm transition-colors"
            >
              隨機混音設定
            </button>
            <button 
              onClick={randomMix}
              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded-md text-white text-sm transition-colors"
            >
              隨機混音
            </button>
            <button 
              onClick={clearCurrentMix}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded-md text-white text-sm transition-colors"
            >
              清除混音
            </button>
          </div>
        </div>

        {/* 當前播放的音軌顯示 */}
        <div className="current-mix mb-4 p-3 bg-gray-700 rounded-md">
          <h4 className="text-sm font-medium text-gray-300 mb-2">當前播放:</h4>
          <div className="flex flex-wrap gap-2">
            {activeTracks.length > 0 ? (
              activeTracks.map(track => {
                const trackIndex = tracks.findIndex(t => t.path === track.path);
                return (
                  <div key={track.path} className="px-2 py-1 bg-gray-600 text-sm text-white rounded-md flex items-center">
                    {track.name}
                    <button 
                      onClick={() => toggleLock(trackIndex)}
                      className={`ml-1 transition-colors ${
                        track.locked 
                          ? 'text-yellow-500 hover:text-yellow-600' 
                          : 'text-gray-400 hover:text-yellow-500'
                      }`}
                      aria-label={track.locked ? '解除鎖定' : '鎖定'}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button 
                      onClick={() => togglePlay(trackIndex)}
                      className="ml-2 text-gray-300 hover:text-red-400 transition-colors"
                      aria-label="停止播放"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                );
              })
            ) : (
              <span className="text-gray-400 text-sm">無播放中的音軌</span>
            )}
          </div>
        </div>

        {showSaveDialog && (
          <div className="save-dialog mb-4 flex items-center">
            <input
              type="text"
              value={currentPresetName}
              onChange={(e) => setCurrentPresetName(e.target.value)}
              placeholder="輸入混音名稱"
              className="flex-grow p-2 bg-gray-700 text-white rounded-l-md border border-gray-600 focus:outline-none focus:border-teal-500"
            />
            <button 
              onClick={saveMixPreset}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-r-md text-white transition-colors"
            >
              儲存
            </button>
          </div>
        )}

        {showExcludeDialog && (
          <div className="exclude-dialog mb-4 p-3 bg-gray-700 rounded-md">
            <h4 className="text-sm font-medium text-gray-300 mb-2">排除以下類別:</h4>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => toggleExcludeCategory(category.id)}
                  className={`px-2 py-1 text-sm rounded-md transition-colors ${
                    excludedCategories.includes(category.id)
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  }`}
                >
                  {category.name}
                  {excludedCategories.includes(category.id) && ' ✓'}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-600">
          <h4 className="text-sm font-medium text-gray-300 mb-3">已儲存的混音預設:</h4>
          <div className="presets-list grid grid-cols-2 md:grid-cols-3 gap-2">
            {mixPresets.map(preset => (
              <div 
                key={preset.id} 
                className="preset-item flex items-center bg-gray-700 rounded-md overflow-hidden shadow-md hover:shadow-lg transition-shadow"
              >
                <button 
                  onClick={() => loadMixPreset(preset)}
                  className="flex-grow px-3 py-2 text-left hover:bg-gray-600 text-white transition-colors"
                >
                  {preset.name}
                </button>
                <button 
                  onClick={() => deleteMixPreset(preset.id)}
                  className="px-2 py-2 bg-red-600 hover:bg-red-700 text-white transition-colors"
                >
                  ×
                </button>
              </div>
            ))}
            {mixPresets.length === 0 && (
              <p className="text-gray-400 text-sm col-span-full">尚無儲存的混音預設</p>
            )}
          </div>
        </div>
      </div>

      {/* 分類選擇器 */}
      <div className="category-selector mb-6 flex flex-wrap gap-2">
        <button 
          onClick={() => setActiveCategory('all')}
          className={`px-3 py-1 rounded-md transition-colors ${
            activeCategory === 'all' 
              ? 'bg-teal-600 text-white' 
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          全部
        </button>
        {categories.map(category => (
          <button 
            key={category.id}
            onClick={() => setActiveCategory(category.id)}
            className={`px-3 py-1 rounded-md transition-colors ${
              activeCategory === category.id 
                ? 'bg-teal-600 text-white' 
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            {category.name}
          </button>
        ))}
      </div>

      {/* 音軌列表 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredTracks.map((track, index) => {
          const originalIndex = tracks.findIndex(t => t.path === track.path);
          return (
            <div 
              key={track.path} 
              className={`track-item p-4 bg-gray-800 rounded-lg border transition-all duration-300 ${
                track.locked 
                  ? 'border-yellow-500' 
                  : track.isPlaying 
                    ? 'border-teal-500' 
                    : 'border-gray-700 hover:border-teal-500'
              } ${track.isCustom ? 'relative' : ''}`}
            >
              {track.isCustom && (
                <div className="absolute top-2 right-2 flex space-x-2">
                  <button
                    onClick={() => {
                      if (track.audioData) {
                        // 創建下載連結
                        const a = document.createElement('a');
                        a.href = track.audioData;
                        a.download = `${track.name}.webm`;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-700 p-1 rounded-full text-white transition-colors"
                    aria-label="下載自定義音訊"
                    title="下載音訊"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <button
                    onClick={() => deleteCustomAudio(track.path)}
                    className="bg-red-600 hover:bg-red-700 p-1 rounded-full text-white transition-colors"
                    aria-label="刪除自定義音訊"
                    title="刪除音訊"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center">
                  <h3 className="text-lg font-medium text-white">
                    {track.name}
                    {track.isCustom && (
                      <span className="ml-2 text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">
                        自定義
                      </span>
                    )}
                  </h3>
                  <button
                    onClick={() => toggleLock(originalIndex)}
                    className={`ml-2 p-1 rounded-full transition-colors ${
                      track.locked 
                        ? 'text-yellow-500 hover:text-yellow-600' 
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                    aria-label={track.locked ? '解除鎖定' : '鎖定'}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* 播放模式選擇器 */}
                  <div className="ml-2 flex items-center">
                    <div className="relative group">
                      <select
                        value={track.playbackMode || PlaybackMode.LOOP}
                        onChange={(e) => {
                          const newTracks = [...tracks];
                          const trackToUpdate = newTracks[originalIndex];
                          trackToUpdate.playbackMode = e.target.value as PlaybackMode;
                          
                          // 如果音訊正在播放，需要重新設置播放模式
                          if (trackToUpdate.isPlaying) {
                            const audio = audioElementsRef.current[trackToUpdate.path];
                            if (audio) {
                              // 清除之前的事件監聽器
                              audio.onended = null;
                              // 設置新的播放模式
                              setupAudioPlayback(audio, trackToUpdate);
                            }
                          }
                          
                          setTracks(newTracks);
                        }}
                        className="text-xs bg-gray-700 text-gray-300 border border-gray-600 rounded px-1 py-0.5"
                      >
                        <option value={PlaybackMode.LOOP}>循環</option>
                        <option value={PlaybackMode.SMART_LOOP}>智能循環</option>
                        <option value={PlaybackMode.ONCE}>單次</option>
                        <option value={PlaybackMode.INTERVAL}>間隔</option>
                      </select>
                      
                      {/* 播放模式說明提示 */}
                      <div className="absolute left-0 bottom-full mb-2 w-48 bg-gray-900 text-xs text-gray-300 p-2 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
                        {track.playbackMode === PlaybackMode.LOOP && (
                          <p>循環播放：持續重複播放音訊</p>
                        )}
                        {track.playbackMode === PlaybackMode.SMART_LOOP && (
                          <p>智能循環：隨機調整音量(±40%)並偶爾暫停(1-60秒)，創造自然變化</p>
                        )}
                        {track.playbackMode === PlaybackMode.ONCE && (
                          <p>單次播放：僅播放一次後停止</p>
                        )}
                        {track.playbackMode === PlaybackMode.INTERVAL && (
                          <p>間隔播放：播放完畢後等待一段時間再重新播放</p>
                        )}
                      </div>
                    </div>
                    
                    {/* 間隔設定按鈕 */}
                    {track.playbackMode === PlaybackMode.INTERVAL && (
                      <button
                        onClick={() => {
                          setSelectedTrackForInterval(originalIndex);
                          setIntervalMinDelay(track.intervalSettings?.minDelay || 5);
                          setIntervalMaxDelay(track.intervalSettings?.maxDelay || 15);
                          setShowIntervalSettings(true);
                        }}
                        className="ml-1 p-1 rounded-full text-gray-400 hover:text-teal-400 transition-colors"
                        aria-label="設定間隔時間"
                        title="設定間隔時間"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <button 
                  onClick={() => togglePlay(originalIndex)}
                  className={`play-button p-2 rounded-full ${track.isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-teal-500 hover:bg-teal-600'} transition-colors duration-200`}
                  aria-label={track.isPlaying ? '暫停' : '播放'}
                >
                  {track.isPlaying ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
              <div className="volume-control flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071a1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243a1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828a1 1 0 010-1.415z" clipRule="evenodd" />
                </svg>
                <input 
                  type="range" 
                  min="0" 
                  max="1" 
                  step="0.01" 
                  value={track.volume} 
                  onChange={(e) => handleVolumeChange(originalIndex, e)} 
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AudioPlayer;